import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import './styles/select.css';

interface SelectContextType {
  value?: string;
  onValueChange?: (value: string) => void;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  // Expose the container ref so floating content can be positioned outside scrolling parents
  containerRef: React.RefObject<HTMLDivElement>;
}

const SelectContext = React.createContext<SelectContextType>({
  isOpen: false,
  setIsOpen: () => {},
  // default ref for contexts that are used outside of a provider (e.g., tests)
  containerRef: React.createRef<HTMLDivElement>(),
});

interface SelectProps {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  children: React.ReactNode;
  disabled?: boolean;
}

export const Select: React.FC<SelectProps> = ({
  value,
  defaultValue,
  onValueChange,
  children,
  disabled,
}) => {
  const [internalValue, setInternalValue] = useState<string | undefined>(value ?? defaultValue);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value !== undefined) {
      setInternalValue(value);
    }
  }, [value]);

  const handleValueChange = (newValue: string) => {
    setInternalValue(newValue);
    onValueChange?.(newValue);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <SelectContext.Provider
      value={{ value: internalValue, onValueChange: handleValueChange, isOpen, setIsOpen, containerRef }}
    >
      <div ref={containerRef} className="relative w-full">
        {React.Children.map(children, (child) => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child as React.ReactElement<SelectTriggerProps>, {
              disabled,
            });
          }
          return child;
        })}
      </div>
    </SelectContext.Provider>
  );
};

interface SelectTriggerProps {
  children: React.ReactNode;
  disabled?: boolean;
  className?: string;
}

const ChevronIcon: React.FC<{ isOpen: boolean }> = ({ isOpen }) => (
  <svg
    className={cn(
      'w-4 h-4 text-muted transition-transform duration-200',
      isOpen && 'rotate-180'
    )}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

export const SelectTrigger: React.FC<SelectTriggerProps> = ({ children, disabled, className }) => {
  const { isOpen, setIsOpen } = React.useContext(SelectContext);

  return (
    <button
      onClick={() => !disabled && setIsOpen(!isOpen)}
      disabled={disabled}
      className={cn('form-select-trigger', className)}
      type="button"
    >
      {children}
      <ChevronIcon isOpen={isOpen} />
    </button>
  );
};

interface SelectValueProps {
  placeholder?: string;
  className?: string;
  children?: React.ReactNode;
}

export const SelectValue: React.FC<SelectValueProps> = ({ placeholder, className, children }) => {
  const { value } = React.useContext(SelectContext);

  if (children) {
    return <span className={className}>{children}</span>;
  }

  return <span className={className}>{value || placeholder || 'Select...'}</span>;
};

interface SelectContentProps {
  children: React.ReactNode;
  className?: string;
}

export const SelectContent: React.FC<SelectContentProps> = ({ children, className }) => {
  const { isOpen, containerRef } = React.useContext(SelectContext);
  const [style, setStyle] = useState<React.CSSProperties>({});

  useEffect(() => {
    if (!isOpen || !containerRef?.current) return;

    const updatePosition = () => {
      const rect = containerRef.current!.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const desiredTop = rect.bottom;
      const maxHeight = Math.min(360, viewportHeight - rect.bottom - 16); // limit height
      let top = desiredTop;
      let transformOrigin = 'top';

      // If not enough space below, position above
      if (rect.bottom + 160 > viewportHeight - 16) {
        top = rect.top - 8; // small offset
        transformOrigin = 'bottom';
      }

      // Clamp left so the dropdown doesn't overflow the viewport horizontally
      const viewportWidth = window.innerWidth;
      const computedWidth = rect.width;

      // Use the trigger width but do not allow the dropdown to extend beyond the viewport.
      const widthToUse = Math.min(computedWidth, viewportWidth - 16);

      let left = rect.left;
      if (left + widthToUse > viewportWidth - 8) {
        left = Math.max(8, viewportWidth - widthToUse - 8);
      }

      setStyle({ position: 'fixed', top: `${top}px`, left: `${left}px`, width: `${widthToUse}px`, zIndex: 200000, transformOrigin, maxHeight, overflow: 'auto', right: 'auto' });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, containerRef]);

  if (!isOpen) return null;

  // Render in a portal to avoid clipping inside scrollable parents (modals, dialogs, etc.)
  return typeof document !== 'undefined' ? createPortal(
    // Prevent mousedown on the portal content from bubbling to document and
    // triggering the 'click outside' handler that closes the dropdown before
    // the item click can be processed.
    <div className={cn('form-select-content', className)} style={style} onMouseDown={e => e.stopPropagation()} onTouchStart={e => e.stopPropagation()}>
      {children}
    </div>,
    document.body
  ) : null;
};

interface SelectItemProps {
  value: string;
  children: React.ReactNode;
  className?: string;
}

export const SelectItem: React.FC<SelectItemProps> = ({ value, children, className }) => {
  const { value: selectedValue, onValueChange, setIsOpen } = React.useContext(SelectContext);
  const isSelected = selectedValue === value;

  const handleClick = () => {
    onValueChange?.(value);
    setIsOpen(false);
  };

  return (
    <div
      className={cn(
        'form-select-item',
        isSelected ? 'form-select-item-selected' : 'form-select-item-idle',
        className
      )}
      onClick={handleClick}
    >
      {children}
    </div>
  );
};
